"use client";

import { useEffect, useRef, useState, useSyncExternalStore, type FormEvent, type MouseEvent, type PointerEvent } from "react";
import type { CampusLocation } from "@repo/actions/AcademyApi/GetCampuses";
import { geocodeAddress } from "@repo/actions/GoogleMaps/GeocodeAddress";
import { Typography } from "@repo/ui/aops-components/typography";
import { Button, Modal, TextInput } from "@repo/ui/mantine";
import { ArrowRight, Locate, Search, X } from "lucide-react";
import { CampusList } from "./CampusList/CampusList";
import styles from "./CampusLocationSelector.module.css";
import { getCampusesForSearchOrigin, isBrowserGeolocationDenied, requestBrowserGeoLocation } from "./location";
import { getCloudflareGeoLocation } from "./serverUtils";
import { getKeyedCopy, type KeyedCopyEntry } from "@repo/utils/keyedCopy";
import { COPY } from "./CampusLocationCopyConstants";

// These set the height and width of the bottom sheet modal.
const BOTTOM_SHEET_MQ = "(max-width: 1023px)";
const SHEET_DEFAULT_HEIGHT_RATIO = 0.92;
const SHEET_MAX_HEIGHT_RATIO = 0.92;
const SHEET_MIN_HEIGHT_RATIO = 0.35;
function subscribeBottomSheet(onStoreChange: () => void) {
  const mql = window.matchMedia(BOTTOM_SHEET_MQ);
  mql.addEventListener("change", onStoreChange);
  return () => mql.removeEventListener("change", onStoreChange);
}
function getBottomSheetSnapshot() {
  return window.matchMedia(BOTTOM_SHEET_MQ).matches;
}
function useIsCampusModalBottomSheet() {
  return useSyncExternalStore(subscribeBottomSheet, getBottomSheetSnapshot, _temp);
}
function _temp() {
  return false;
}
type SearchOrigin = {
  latitude: number;
  longitude: number;
  /** Set when the geocode result is a US state (abbr from "TX" or "Texas"). */
  stateCode: string | null;
};
export type CampusLocationSelectorProps = {
  campuses: CampusLocation[];
  /**
   * Used for element ids / uniqueness when multiple instances mount.
   * Defaults to "default".
   */
  modalityName?: string;
  keyedCopy?: KeyedCopyEntry[];

  /**
   * When true, pending highlight can include multiple campuses and confirm
   * returns `string[]`. Default false (single-select / existing course pages).
   */
  multiple?: boolean;

  /**
   * `modal` (default): link/trigger opens a dialog — course pages.
   * `inline`: search + list always visible — rec-tool question layout.
   */
  presentation?: "modal" | "inline";

  /** Confirmed selection for single-select mode. */
  value?: string | null;
  /** Confirmed selection for multi-select mode. */
  values?: readonly string[];

  /**
   * Called when the user confirms (modal footer). Single-select: one campus id.
   * Multi-select: one or more campus ids. Optional when {@link presentation}
   * is `inline` (selection is driven by {@link onPendingChange}).
   */
  onConfirm?: (selection: string | string[]) => void | Promise<void>;

  /**
   * When set, scrolled into view after empty-state "Explore online".
   * Defaults to `formats` for course pages; pass `null` to hide that CTA.
   */
  exploreOnlineSectionId?: string | null;

  /**
   * Optional secondary callback when a campus is only highlighted
   * (not yet confirmed). Useful for live previews of multi-select / inline.
   */
  onPendingChange?: (pendingCampusIds: readonly string[]) => void;

  /** When false, hides campus details links. Defaults to true. */
  showCampusDetails?: boolean;
  /** When false, hides map links. Defaults to true. */
  showViewOnMap?: boolean;
};
function toDomIdFragment(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
function confirmedIdsFromProps(multiple: boolean, value: string | null | undefined, values: readonly string[] | undefined): string[] {
  if (multiple) {
    return values ? [...values] : [];
  }
  return value ? [value] : [];
}

/**
 * Shared campus location picker (search, geolocation, region list, confirm).
 * Controlled: consumers pass confirmed value(s) and handle onConfirm.
 */
export function CampusLocationSelector({
  campuses,
  modalityName = "default",
  keyedCopy = [],
  multiple = false,
  presentation = "modal",
  value = null,
  values,
  onConfirm,
  exploreOnlineSectionId = "formats",
  onPendingChange,
  showCampusDetails = true,
  showViewOnMap = true
}: CampusLocationSelectorProps) {
  const isInline = presentation === "inline";
  const isBottomSheet = useIsCampusModalBottomSheet();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOrigin, setSearchOrigin] = useState<SearchOrigin | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const [isGeolocationDenied, setIsGeolocationDenied] = useState(false);
  const [pendingCampusIds, setPendingCampusIds] = useState<string[]>(() => isInline ? confirmedIdsFromProps(multiple, value, values) : []);
  const [sheetHeightPx, setSheetHeightPx] = useState<number | null>(null);
  const [prevIsBottomSheet, setPrevIsBottomSheet] = useState(isBottomSheet);
  const sheetDragRef = useRef<{
    pointerId: number;
    startY: number;
    startHeight: number;
    currentHeight: number;
  } | null>(null);
  const confirmedIds = confirmedIdsFromProps(multiple, value, values);
  // Stable string so effect doesn't re-fire every render when parent passes a
  // freshly allocated `values` array with the same ids.
  const confirmedKey = multiple ? confirmedIds.join("\0") : value ?? "";
  const selectedCampuses = campuses.filter(({
    campus_id
  }) => confirmedIds.includes(campus_id));
  const displayedCampuses = searchOrigin ? getCampusesForSearchOrigin(campuses, searchOrigin.latitude, searchOrigin.longitude, searchOrigin.stateCode) : campuses.map(campus => ({
    campus,
    distanceMiles: null
  }));
  const idFragment = toDomIdFragment(modalityName);
  const changeLinkText = getKeyedCopy(keyedCopy, COPY.changeLink);
  const selectCampusLinkText = getKeyedCopy(keyedCopy, COPY.selectCampusLink);
  const campusModalTitle = getKeyedCopy(keyedCopy, COPY.header);
  const searchPlaceholder = getKeyedCopy(keyedCopy, COPY.searchPlaceholder);
  const searchButtonText = getKeyedCopy(keyedCopy, COPY.searchButton);
  const useLocationText = getKeyedCopy(keyedCopy, COPY.useLocation);
  const viewOnMapText = getKeyedCopy(keyedCopy, COPY.viewOnMap);
  const campusDetailsText = getKeyedCopy(keyedCopy, COPY.campusDetails);
  const showAllCampusesText = getKeyedCopy(keyedCopy, COPY.showAll);
  const confirmCampusText = getKeyedCopy(keyedCopy, COPY.confirm);
  const noResultsText = getKeyedCopy(keyedCopy, COPY.noResults);
  const noResultsTitleTemplate = getKeyedCopy(keyedCopy, COPY.noResultsTitle);
  const noResultsTitle = noResultsTitleTemplate.replace("{{query}}", searchQuery);
  const noResultsBody = getKeyedCopy(keyedCopy, COPY.noResultsBody);
  const exploreOnlineText = getKeyedCopy(keyedCopy, COPY.exploreOnline);
  const locationSearchQueryText = getKeyedCopy(keyedCopy, COPY.locationSearchQuery);
  const locationUnavailableText = getKeyedCopy(keyedCopy, COPY.locationUnavailable);
  const resultsWithinText = getKeyedCopy(keyedCopy, COPY.resultsWithin);
  const locationsFoundOneText = getKeyedCopy(keyedCopy, COPY.locationsFoundOne);
  const locationsFoundManyText = getKeyedCopy(keyedCopy, COPY.locationsFoundMany);
  const unknownRegionText = getKeyedCopy(keyedCopy, COPY.unknownRegion);
  const distanceMilesText = getKeyedCopy(keyedCopy, COPY.distanceMiles);
  const emptyStateText = searchError ?? noResultsText;
  const hasNoSearchResults = Boolean(searchOrigin && displayedCampuses.length === 0);
  const updatePending = (next: string[]) => {
    setPendingCampusIds(next);
    onPendingChange?.(next);
  };

  // Inline layouts keep pending selection aligned with the controlled value.
  useEffect(() => {
    if (!isInline) {
      return;
    }
    setPendingCampusIds(confirmedIdsFromProps(multiple, value, values));
  }, [isInline, multiple, confirmedKey, value, values]);
  useEffect(() => {
    let cancelled = false;
    void isBrowserGeolocationDenied().then(denied => {
      if (!cancelled) {
        setIsGeolocationDenied(denied);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Resync the sheet height when the viewport crosses the bottom-sheet
  // breakpoint while the modal is open. Adjusted during render (rather than
  // in an effect) per https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (isBottomSheet !== prevIsBottomSheet) {
    setPrevIsBottomSheet(isBottomSheet);
    if (isModalOpen) {
      setSheetHeightPx(isBottomSheet ? Math.round(window.innerHeight * SHEET_DEFAULT_HEIGHT_RATIO) : null);
    }
  }
  const resetSearchState = () => {
    setSearchDraft("");
    setSearchQuery("");
    setSearchOrigin(null);
    setSearchError(null);
    setIsSearching(false);
  };
  const openModal = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (campuses.length) {
      resetSearchState();
      updatePending(confirmedIds);
      if (isBottomSheet) {
        setSheetHeightPx(Math.round(window.innerHeight * SHEET_DEFAULT_HEIGHT_RATIO));
      }
      setIsModalOpen(true);
    }
  };
  const closeModal = () => {
    setIsModalOpen(false);
    updatePending([]);
    setSheetHeightPx(null);
    sheetDragRef.current = null;
  };
  const clampSheetHeight = (height: number) => {
    const maxHeight = window.innerHeight * SHEET_MAX_HEIGHT_RATIO;
    const minHeight = window.innerHeight * SHEET_MIN_HEIGHT_RATIO;
    return Math.round(Math.min(maxHeight, Math.max(minHeight, height)));
  };
  const handleSheetDragPointerDown = (event_0: PointerEvent<HTMLDivElement>) => {
    if (!isBottomSheet) {
      return;
    }
    const startHeight = sheetHeightPx ?? Math.round(window.innerHeight * SHEET_DEFAULT_HEIGHT_RATIO);
    sheetDragRef.current = {
      pointerId: event_0.pointerId,
      startY: event_0.clientY,
      startHeight,
      currentHeight: startHeight
    };
    event_0.currentTarget.setPointerCapture(event_0.pointerId);
  };
  const handleSheetDragPointerMove = (event_1: PointerEvent<HTMLDivElement>) => {
    const drag = sheetDragRef.current;
    if (!drag || drag.pointerId !== event_1.pointerId) {
      return;
    }

    // Dragging up increases height; dragging down decreases it.
    const nextHeight = drag.startHeight + (drag.startY - event_1.clientY);
    const clampedHeight = clampSheetHeight(nextHeight);
    drag.currentHeight = clampedHeight;
    setSheetHeightPx(clampedHeight);
  };
  const handleSheetDragPointerUp = (event_2: PointerEvent<HTMLDivElement>) => {
    const drag_0 = sheetDragRef.current;
    if (!drag_0 || drag_0.pointerId !== event_2.pointerId) {
      return;
    }
    const currentHeight = drag_0.currentHeight;
    sheetDragRef.current = null;
    if (event_2.currentTarget.hasPointerCapture(event_2.pointerId)) {
      event_2.currentTarget.releasePointerCapture(event_2.pointerId);
    }
    setSheetHeightPx(clampSheetHeight(currentHeight));
  };
  const handleExploreOnlineFormats = () => {
    closeModal();
    if (!exploreOnlineSectionId) {
      return;
    }
    requestAnimationFrame(() => {
      document.getElementById(exploreOnlineSectionId)?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    });
  };
  const handleCampusConfirm = async () => {
    if (!pendingCampusIds.length || !onConfirm) {
      return;
    }
    if (multiple) {
      await onConfirm([...pendingCampusIds]);
    } else {
      const only = pendingCampusIds[0];
      if (!only) {
        return;
      }
      await onConfirm(only);
    }
    closeModal();
  };
  const handleCampusHighlight = (campusId: string) => {
    if (multiple) {
      updatePending(pendingCampusIds.includes(campusId) ? pendingCampusIds.filter(id => id !== campusId) : [...pendingCampusIds, campusId]);
      return;
    }
    updatePending([campusId]);
  };
  const handleSearchSubmit = (event_3: FormEvent<HTMLFormElement>) => {
    event_3.preventDefault();
    void runLocationSearch(searchDraft);
  };
  const runLocationSearch = async (query: string) => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      setSearchQuery("");
      setSearchOrigin(null);
      setSearchError(null);
      return;
    }
    if (isSearching || !campuses.length) {
      return;
    }
    setIsSearching(true);
    setSearchError(null);
    updatePending([]);
    try {
      const result = await geocodeAddress(normalizedQuery);
      if (!result.success) {
        setSearchQuery("");
        setSearchOrigin(null);
        setSearchError(result.errorMessage);
        return;
      }
      setSearchQuery(normalizedQuery);
      setSearchOrigin({
        latitude: result.latitude,
        longitude: result.longitude,
        stateCode: result.stateCode
      });
      setSearchError(null);
    } finally {
      setIsSearching(false);
    }
  };
  const handleUseCurrentLocation = () => {
    if (isResolvingLocation || isSearching || !campuses.length) {
      return;
    }

    // Start getCurrentPosition in this click turn so the browser can show
    // the location permission prompt (requires user activation).
    const browserLocationPromise = requestBrowserGeoLocation();
    setIsResolvingLocation(true);
    setSearchError(null);
    updatePending([]);
    void (async () => {
      try {
        const {
          location: browserLocation,
          permissionDenied
        } = await browserLocationPromise;
        if (permissionDenied) {
          setIsGeolocationDenied(true);
        }
        const location = browserLocation ?? (await getCloudflareGeoLocation());
        if (!location) {
          if (!permissionDenied) {
            setSearchQuery("");
            setSearchOrigin(null);
            setSearchError(locationUnavailableText);
          }
          return;
        }
        setSearchDraft("");
        setSearchQuery(locationSearchQueryText);
        setSearchOrigin({
          latitude: location.latitude,
          longitude: location.longitude,
          stateCode: null
        });
        setSearchError(null);
      } finally {
        setIsResolvingLocation(false);
      }
    })();
  };
  const triggerLabel = (() => {
    if (!selectedCampuses.length) {
      return selectCampusLinkText;
    }
    if (selectedCampuses.length === 1) {
      return selectedCampuses[0]!.short_name;
    }
    return `${selectedCampuses.length} campuses`;
  })();
  const selectorButton = <button id={`campus-location-selector-button-${idFragment}`} type="button" className={styles.selectorButton} disabled={!campuses.length} onClick={openModal}>
			{selectedCampuses.length ? changeLinkText : selectCampusLinkText}
		</button>;
  const stopReactTreePropagation = (event_4: MouseEvent) => {
    event_4.stopPropagation();
  };
  const campusList = <CampusList campuses={displayedCampuses} highlightedCampusIds={pendingCampusIds} multiple={multiple} viewOnMapText={viewOnMapText} campusDetailsText={campusDetailsText} showAllCampusesText={showAllCampusesText} resultsWithinText={resultsWithinText} locationsFoundOneText={locationsFoundOneText} locationsFoundManyText={locationsFoundManyText} unknownRegionText={unknownRegionText} distanceMilesText={distanceMilesText} showCampusDetails={showCampusDetails} showViewOnMap={showViewOnMap} listLayout={isInline ? "page" : "contained"} listScrollKey={searchQuery} searchResults={searchOrigin ? {
    isDistanceLimited: searchOrigin.stateCode == null
  } : null} onShowAllCampuses={resetSearchState} onCampusHighlight={handleCampusHighlight} emptyState={hasNoSearchResults ? <div className={styles.noResultsEmptyState}>
						<div className={styles.noResultsCopy}>
							<Typography as="p" variant="body" className={styles.noResultsTitle}>
								{noResultsTitle}
							</Typography>
							<Typography as="p" variant="bodySmall" className={styles.noResultsBody}>
								{noResultsBody}
							</Typography>
						</div>
						{exploreOnlineSectionId ? <button type="button" className={styles.exploreOnlineButton} onClick={handleExploreOnlineFormats}>
								<Typography as="span" variant="buttonMd">
									{exploreOnlineText}
								</Typography>
								<ArrowRight aria-hidden size={16} />
							</button> : null}
					</div> : null} />;
  const searchRow = <form className={styles.searchRow} onSubmit={handleSearchSubmit}>
			<TextInput id={`campus-location-selector-search-${idFragment}`} aria-label={searchPlaceholder} classNames={{
      root: styles.searchInputRoot,
      wrapper: styles.searchInputWrapper,
      input: styles.searchInput,
      section: styles.searchInputSection
    }} leftSection={<Search aria-hidden size={16} strokeWidth={1.5} />} leftSectionPointerEvents="none" rightSection={searchDraft ? <button type="button" className={styles.searchClearButton} aria-label="Clear search" disabled={isSearching} onClick={() => {
      resetSearchState();
      updatePending(confirmedIds);
    }}>
							<X aria-hidden size={16} strokeWidth={1.5} />
						</button> : null} rightSectionPointerEvents="all" placeholder={searchPlaceholder} value={searchDraft} disabled={isSearching} onChange={event_5 => setSearchDraft(event_5.currentTarget.value)} />
			<Button type="submit" variant="default" radius="xl" className={styles.searchButton} disabled={isSearching || !campuses.length} loading={isSearching}>
				<Typography as="span" variant="buttonMd">
					{searchButtonText}
				</Typography>
			</Button>
		</form>;
  const locationControl = isGeolocationDenied ? null : <button type="button" className={styles.locationButton} disabled={isResolvingLocation || isSearching || !campuses.length} onClick={handleUseCurrentLocation}>
			<Locate aria-hidden className={styles.locationButtonIcon} size={16} />
			<Typography as="span" variant="buttonMd">
				{useLocationText}
			</Typography>
		</button>;
  const results = searchError ? <Typography as="p" variant="body" className={styles.emptyState}>
			{emptyStateText}
		</Typography> : searchOrigin || displayedCampuses.length > 0 ? <div className={isInline ? styles.campusListSectionInline : styles.campusListSection}>
			{campusList}
		</div> : <Typography as="p" variant="body" className={styles.emptyState}>
			{emptyStateText}
		</Typography>;
  if (isInline) {
    return <div className={styles.inlineRoot} id={`campus-location-selector-${idFragment}`}>
				<div className={styles.inlineFunctionalUI}>
					{searchRow}
					{locationControl}
				</div>
				{results}
			</div>;
  }
  return <>
			{selectedCampuses.length > 0 ? <span className={styles.selectorInline}>
					<span>{triggerLabel}</span>
					{selectorButton}
				</span> : selectorButton}

			<Modal centered={!isBottomSheet} opened={isModalOpen} onClose={closeModal} onClick={stopReactTreePropagation} onMouseDown={stopReactTreePropagation} size={isBottomSheet ? "100%" : "lg"} withCloseButton={!isBottomSheet} transitionProps={{
      transition: isBottomSheet ? "slide-up" : "fade",
      duration: 200
    }} title={<div className={styles.modalTitleBlock}>
						{isBottomSheet ? <div className={styles.modalDragHandleArea} role="separator" aria-orientation="horizontal" aria-label="Resize campus selector" onPointerDown={handleSheetDragPointerDown} onPointerMove={handleSheetDragPointerMove} onPointerUp={handleSheetDragPointerUp} onPointerCancel={handleSheetDragPointerUp}>
								<div className={styles.modalDragHandle} aria-hidden />
							</div> : null}
						<Typography as="span" variant={"h5"} className={styles.modalTitle}>
							{campusModalTitle}
						</Typography>
					</div>} classNames={{
      inner: styles.modalInner,
      content: styles.modalDialog,
      header: styles.modalHeader,
      body: styles.modalContent,
      close: styles.modalCloseButton
    }} styles={isBottomSheet && sheetHeightPx != null ? {
      content: {
        height: `${sheetHeightPx}px`,
        maxHeight: `${Math.round(window.innerHeight * SHEET_MAX_HEIGHT_RATIO)}px`
      }
    } : undefined} closeButtonProps={{
      "aria-label": "Close",
      icon: <X aria-hidden size={20} strokeWidth={1.5} />
    }}>
				<div className={styles.modalShell} onClick={stopReactTreePropagation} onMouseDown={stopReactTreePropagation}>
					<div className={styles.modalBody}>
						{searchRow}
						{locationControl}
						{results}
					</div>

					<div className={styles.modalFooter}>
						<button type="button" className={styles.confirmCampusButton} disabled={!pendingCampusIds.length} onClick={() => {
            void handleCampusConfirm();
          }}>
							<Typography as="span" variant="buttonMd">
								{confirmCampusText}
							</Typography>
						</button>
					</div>
				</div>
			</Modal>
		</>;
}